import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { 
  BarChart3, TrendingUp, Users, Calendar, 
  CheckCircle, XCircle, Clock, AlertTriangle,
  DollarSign, Target, Award, User, Download,
  Filter, CalendarDays, RefreshCw
} from 'lucide-react';
import * as XLSX from 'xlsx';

const API = process.env.REACT_APP_BACKEND_URL;

const StatistiquesPage = () => {
  const [loading, setLoading] = useState(true);
  const [teleprosStats, setTeleprosStats] = useState([]);
  const [commerciauxStats, setCommerciauxStats] = useState([]);
  const [allTelepros, setAllTelepros] = useState([]);
  const [allCommerciaux, setAllCommerciaux] = useState([]);
  const [globalStats, setGlobalStats] = useState({
    total_rdv: 0,
    total_signes: 0,
    taux_transformation: 0,
    ca_total: 0
  });

  // États pour les filtres de date
  const [dateFilter, setDateFilter] = useState('annee_courante');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => {
    fetchStatistiques();
  }, [dateFilter, dateDebut, dateFin, selectedMonth]);

  // Initialiser les dates par défaut
  useEffect(() => {
    const now = new Date();
    const debutAnnee = new Date(now.getFullYear(), 0, 1);
    setDateDebut(debutAnnee.toISOString().split('T')[0]);
    setDateFin(now.toISOString().split('T')[0]);
  }, []);

  const fetchStatistiques = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Récupérer tous les utilisateurs
      const usersResponse = await axios.get(`${API}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Récupérer tous les RDV
      const rdvResponse = await axios.get(`${API}/api/rendez-vous`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Récupérer tous les prospects
      const prospectsResponse = await axios.get(`${API}/api/prospects`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Récupérer tous les clients (via details pour éviter erreur 500)
      let clients = [];
      try {
        const clientsResponse = await axios.get(`${API}/api/clients/details`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        clients = clientsResponse.data;
      } catch (error) {
        console.error('Error fetching clients, using empty array:', error);
        clients = [];
      }
      
      // Récupérer tous les contrats pour calculer le CA
      let contracts = [];
      try {
        const contractsResponse = await axios.get(`${API}/api/contracts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        contracts = contractsResponse.data;
      } catch (error) {
        console.error('Error fetching contracts, using empty array:', error);
        contracts = [];
      }

      const users = usersResponse.data;
      let rdvs = rdvResponse.data;
      let prospects = prospectsResponse.data;

      // Appliquer les filtres de date
      const { debut, fin } = getDateRange();
      
      if (debut && fin) {
        rdvs = rdvs.filter(rdv => {
          const rdvDate = new Date(rdv.date_rdv);
          return rdvDate >= debut && rdvDate <= fin;
        });
        
        prospects = prospects.filter(prospect => {
          if (!prospect.created_at) return true;
          const prospectDate = new Date(prospect.created_at);
          return prospectDate >= debut && prospectDate <= fin;
        });
        
        clients = clients.filter(client => {
          if (!client.created_at) return true;
          const clientDate = new Date(client.created_at);
          return clientDate >= debut && clientDate <= fin;
        });
      }

      // TOUS les Telepros et Commerciaux (même ceux sans activité)
      const telepros = users.filter(user => user.role === 'telepro');
      const commerciaux = users.filter(user => user.role === 'commercial' || user.role === 'regie');

      setAllTelepros(telepros);
      setAllCommerciaux(commerciaux);

      // Calculer statistiques pour TOUS les utilisateurs (même sans activité)
      const teleprosStatsData = telepros.map(telepro => 
        calculateTeleprosStats(telepro, prospects, rdvs, clients, contracts)
      );

      const commerciauxStatsData = commerciaux.map(commercial => 
        calculateCommerciauxStats(commercial, rdvs, clients, contracts)
      );

      // Statistiques globales
      const rdvSignes = rdvs.filter(rdv => rdv.resultat === 'signe').length;
      const totalRdv = rdvs.length;
      
      // Calculer le CA total depuis tous les contrats
      const caTotal = contracts.reduce((sum, contract) => {
        const montantAvance = contract.montant_avance || 0;
        const paiementMensuel = contract.paiement_mensuel || 0;
        const paiementsMensuelsConfig = contract.paiements_mensuels_config || {};
        const dureeMois = paiementsMensuelsConfig.duree_mois || 12;
        const totalPaiementsMensuels = paiementMensuel * dureeMois;
        const avancesSupplementaires = contract.avances_supplementaires || [];
        const totalAvancesSupp = avancesSupplementaires.reduce((s, a) => s + (a.montant || 0), 0);
        const totalContrat = montantAvance + totalPaiementsMensuels + totalAvancesSupp;
        return sum + totalContrat;
      }, 0);

      setTeleprosStats(teleprosStatsData);
      setCommerciauxStats(commerciauxStatsData);
      setGlobalStats({
        total_rdv: totalRdv,
        total_signes: rdvSignes,
        taux_transformation: totalRdv > 0 ? ((rdvSignes / totalRdv) * 100).toFixed(1) : 0,
        ca_total: caTotal
      });

    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour obtenir la plage de dates selon le filtre
  const getDateRange = () => {
    const now = new Date();
    let debut, fin;

    switch (dateFilter) {
      case 'annee_courante':
        debut = new Date(now.getFullYear(), 0, 1);
        fin = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
        break;
      
      case 'trois_mois':
        debut = new Date();
        debut.setMonth(debut.getMonth() - 3);
        fin = now;
        break;
      
      case 'mois_choisi':
        if (selectedMonth) {
          const [year, month] = selectedMonth.split('-');
          debut = new Date(parseInt(year), parseInt(month) - 1, 1);
          fin = new Date(parseInt(year), parseInt(month), 0, 23, 59, 59);
        }
        break;
      
      case 'periode_personnalisee':
        if (dateDebut && dateFin) {
          debut = new Date(dateDebut);
          fin = new Date(dateFin + 'T23:59:59');
        }
        break;
      
      default:
        debut = new Date(now.getFullYear(), 0, 1);
        fin = now;
    }

    return { debut, fin };
  };

  // Fonction d'export XLSX
  const exportToXLSX = () => {
    const wb = XLSX.utils.book_new();
    
    // Feuille Telepros
    const teleprosData = teleprosStats.map(stat => ({
      'Telepro': stat.nom,
      'Prospects Créés': stat.prospects_crees,
      'RDV Planifiés': stat.rdv_planifies,
      'RDV Réalisés': stat.rdv_realises,
      'RDV Signés': stat.rdv_signes,
      'RDV Non Signés': stat.rdv_non_signes,
      'RDV Annulés': stat.rdv_annules,
      'RDV Reportés': stat.rdv_reportes,
      'Clients Convertis': stat.clients_convertis,
      'Taux Transformation (%)': stat.taux_transformation,
      'Taux Conversion Prospects (%)': stat.taux_conversion_prospect
    }));
    
    const wsTelepros = XLSX.utils.json_to_sheet(teleprosData);
    XLSX.utils.book_append_sheet(wb, wsTelepros, 'Statistiques Telepros');
    
    // Feuille Commerciaux
    const commerciauxData = commerciauxStats.map(stat => ({
      'Commercial/Régie': stat.nom,
      'Rôle': stat.role,
      'RDV Planifiés': stat.rdv_planifies,
      'RDV Réalisés': stat.rdv_realises,
      'RDV Signés': stat.rdv_signes,
      'RDV Non Signés': stat.rdv_non_signes,
      'RDV Plantés': stat.rdv_plantes,
      'RDV Reportés': stat.rdv_reportes,
      'RDV Annulés': stat.rdv_annules,
      'Clients Générés': stat.clients_generes,
      'CA Généré (€)': stat.ca_genere,
      'CA Moyen (€)': stat.ca_moyen,
      'Taux Signature (%)': stat.taux_signature
    }));
    
    const wsCommerciaux = XLSX.utils.json_to_sheet(commerciauxData);
    XLSX.utils.book_append_sheet(wb, wsCommerciaux, 'Statistiques Commerciaux');
    
    // Feuille Résumé Global
    const globalData = [{
      'Indicateur': 'Total RDV',
      'Valeur': globalStats.total_rdv
    }, {
      'Indicateur': 'RDV Signés', 
      'Valeur': globalStats.total_signes
    }, {
      'Indicateur': 'Taux Transformation (%)',
      'Valeur': globalStats.taux_transformation
    }, {
      'Indicateur': 'CA Total (€)',
      'Valeur': globalStats.ca_total
    }];
    
    const wsGlobal = XLSX.utils.json_to_sheet(globalData);
    XLSX.utils.book_append_sheet(wb, wsGlobal, 'Résumé Global');
    
    // Export avec nom de fichier avec date
    const { debut, fin } = getDateRange();
    const fileName = `Statistiques_${debut?.toISOString().split('T')[0]}_au_${fin?.toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const calculateTeleprosStats = (telepro, prospects, rdvs, clients, contracts) => {
    // Prospects ASSIGNÉS à ce Telepro
    const prospectsAssigned = prospects.filter(p => 
      (p.assigned_telepro === telepro.id) || 
      (!p.assigned_telepro && p.created_by === telepro.id)
    );
    
    // RDV liés aux prospects ASSIGNÉS
    const rdvsTelepro = rdvs.filter(rdv => 
      prospectsAssigned.some(p => p.id === rdv.prospect_id)
    );
    
    // Clients ASSIGNÉS à ce Telepro
    const clientsAssigned = clients.filter(c => 
      (c.assigned_telepro === telepro.id) || 
      (!c.assigned_telepro && c.created_by === telepro.id)
    );
    
    // NOUVEAU: Calculer le CA pour ce télépro
    const clientTeleproIds = clientsAssigned.map(c => c.id);
    const contractsTelepro = contracts.filter(contract => 
      clientTeleproIds.includes(contract.client_id)
    );
    
    const caTotal = contractsTelepro.reduce((sum, contract) => {
      const montantAvance = contract.montant_avance || 0;
      const paiementMensuel = contract.paiement_mensuel || 0;
      const paiementsMensuelsConfig = contract.paiements_mensuels_config || {};
      const dureeMois = paiementsMensuelsConfig.duree_mois || 12;
      const totalPaiementsMensuels = paiementMensuel * dureeMois;
      const avancesSupplementaires = contract.avances_supplementaires || [];
      const totalAvancesSupp = avancesSupplementaires.reduce((s, a) => s + (a.montant || 0), 0);
      const totalContrat = montantAvance + totalPaiementsMensuels + totalAvancesSupp;
      return sum + totalContrat;
    }, 0);
    
    const rdvPlanifies = rdvsTelepro.filter(r => r.status === 'planifie').length;
    const rdvRealises = rdvsTelepro.filter(r => r.status === 'realise').length;
    const rdvSignes = rdvsTelepro.filter(r => r.resultat === 'signe').length;
    const rdvNonSignes = rdvsTelepro.filter(r => r.resultat === 'non_signe').length;
    const rdvAnnules = rdvsTelepro.filter(r => r.status === 'annule').length;
    const rdvReportes = rdvsTelepro.filter(r => r.status === 'reporte').length;

    return {
      id: telepro.id,
      nom: `${telepro.first_name} ${telepro.last_name}`,
      prospects_crees: prospectsAssigned.length,
      rdv_planifies: rdvPlanifies,
      rdv_realises: rdvRealises,
      rdv_signes: rdvSignes,
      rdv_non_signes: rdvNonSignes,
      rdv_annules: rdvAnnules,
      rdv_reportes: rdvReportes,
      clients_convertis: clientsAssigned.length,
      taux_transformation: rdvRealises > 0 ? ((rdvSignes / rdvRealises) * 100).toFixed(1) : 0,
      taux_conversion_prospect: prospectsAssigned.length > 0 ? ((clientsAssigned.length / prospectsAssigned.length) * 100).toFixed(1) : 0,
      ca_total: caTotal,
      ca_moyen: rdvSignes > 0 ? (caTotal / rdvSignes) : 0
    };
  };

  const calculateCommerciauxStats = (commercial, rdvs, clients, contracts) => {
    // Clients liés à ce commercial via assigned_commercial
    const clientsCommercial = clients.filter(c => c.assigned_commercial === commercial.id);
    
    // RDV assignés à ce commercial
    // Note : Les RDV sont liés aux prospects via prospect_id, pas aux clients
    // Les RDV du commercial = ceux où assigned_to === commercial.id
    const rdvsCommercial = rdvs.filter(r => r.assigned_to === commercial.id);
    
    const rdvPlanifies = rdvsCommercial.filter(r => r.status === 'planifie').length;
    const rdvRealises = rdvsCommercial.filter(r => r.status === 'realise').length;
    const rdvSignes = rdvsCommercial.filter(r => r.resultat === 'signe').length;
    const rdvNonSignes = rdvsCommercial.filter(r => r.resultat === 'non_signe').length;
    const rdvAnnules = rdvsCommercial.filter(r => r.status === 'annule').length;
    const rdvReportes = rdvsCommercial.filter(r => r.status === 'reporte').length;
    const rdvPlantes = rdvsCommercial.filter(r => r.resultat === 'pas_interesse').length;

    // Calculer le CA généré depuis les contrats (TOTAL ENCAISSEMENT PRÉVU)
    const clientIds = clientsCommercial.map(c => c.id);
    const contractsCommercial = contracts.filter(c => clientIds.includes(c.client_id));
    
    const caGenere = contractsCommercial.reduce((sum, contract) => {
      // Calculer le TOTAL ENCAISSEMENT PRÉVU pour chaque contrat
      const montantAvance = contract.montant_avance || 0;
      
      // Paiements mensuels
      const paiementMensuel = contract.paiement_mensuel || 0;
      const paiementsMensuelsConfig = contract.paiements_mensuels_config || {};
      const dureeMois = paiementsMensuelsConfig.duree_mois || 12;
      const totalPaiementsMensuels = paiementMensuel * dureeMois;
      
      // Avances supplémentaires
      const avancesSupplementaires = contract.avances_supplementaires || [];
      const totalAvancesSupp = avancesSupplementaires.reduce((s, a) => s + (a.montant || 0), 0);
      
      const totalContrat = montantAvance + totalPaiementsMensuels + totalAvancesSupp;
      return sum + totalContrat;
    }, 0);

    return {
      id: commercial.id,
      nom: `${commercial.first_name} ${commercial.last_name}`,
      role: commercial.role,
      rdv_planifies: rdvPlanifies,
      rdv_realises: rdvRealises,
      rdv_signes: rdvSignes,
      rdv_non_signes: rdvNonSignes,
      rdv_annules: rdvAnnules,
      rdv_reportes: rdvReportes,
      rdv_plantes: rdvPlantes,
      clients_generes: clientsCommercial.length,
      ca_genere: caGenere,
      taux_signature: rdvRealises > 0 ? ((rdvSignes / rdvRealises) * 100).toFixed(1) : 0,
      ca_moyen: clientsCommercial.length > 0 ? (caGenere / clientsCommercial.length).toFixed(0) : 0
    };
  };

  const getStatColor = (value, type) => {
    if (type === 'taux') {
      if (value >= 70) return 'text-green-600 bg-green-50';
      if (value >= 50) return 'text-yellow-600 bg-yellow-50';
      return 'text-red-600 bg-red-50';
    }
    return 'text-blue-600 bg-blue-50';
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Chargement des statistiques...</div>;
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Statistiques Commerciales</h1>
          <p className="text-gray-600 mt-1">Suivi des performances Telepros et Commerciaux</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={exportToXLSX}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            Export XLSX
          </Button>
          <Button 
            onClick={() => setLoading(true) || fetchStatistiques()}
            variant="outline"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <BarChart3 className="w-8 h-8 text-blue-600" />
        </div>
      </div>

      {/* Filtres de Date */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            Filtres de Période
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Sélection du type de période */}
            <div className="space-y-2">
              <Label>Période</Label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <option value="annee_courante">Année en cours</option>
                <option value="trois_mois">3 derniers mois</option>
                <option value="mois_choisi">Mois spécifique</option>
                <option value="periode_personnalisee">Période personnalisée</option>
              </select>
            </div>

            {/* Sélection mois (si applicable) */}
            {dateFilter === 'mois_choisi' && (
              <div className="space-y-2">
                <Label>Mois</Label>
                <Input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                />
              </div>
            )}

            {/* Dates personnalisées (si applicable) */}
            {dateFilter === 'periode_personnalisee' && (
              <>
                <div className="space-y-2">
                  <Label>Date de début</Label>
                  <Input
                    type="date"
                    value={dateDebut}
                    onChange={(e) => setDateDebut(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de fin</Label>
                  <Input
                    type="date"
                    value={dateFin}
                    onChange={(e) => setDateFin(e.target.value)}
                  />
                </div>
              </>
            )}

            {/* Informations sur la période sélectionnée */}
            <div className="space-y-2">
              <Label>Période active</Label>
              <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-md text-sm">
                <div className="flex items-center gap-1 text-blue-700">
                  <CalendarDays className="w-3 h-3" />
                  {(() => {
                    const { debut, fin } = getDateRange();
                    if (debut && fin) {
                      return `${debut.toLocaleDateString('fr-FR')} au ${fin.toLocaleDateString('fr-FR')}`;
                    }
                    return 'Période non définie';
                  })()}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistiques globales */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total RDV</p>
                <p className="text-2xl font-bold text-gray-900">{globalStats.total_rdv}</p>
              </div>
              <Calendar className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">RDV Signés</p>
                <p className="text-2xl font-bold text-green-600">{globalStats.total_signes}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Taux Transformation</p>
                <p className="text-2xl font-bold text-purple-600">{globalStats.taux_transformation}%</p>
              </div>
              <Target className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">CA Total</p>
                <p className="text-2xl font-bold text-emerald-600">{globalStats.ca_total.toLocaleString()}€</p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Statistiques Telepros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            Statistiques Telepros - Génération de Prospects
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Telepro</th>
                  <th className="text-center p-2">Prospects Créés</th>
                  <th className="text-center p-2">RDV Planifiés</th>
                  <th className="text-center p-2">RDV Réalisés</th>
                  <th className="text-center p-2">Signés</th>
                  <th className="text-center p-2">Non Signés</th>
                  <th className="text-center p-2">Annulés</th>
                  <th className="text-center p-2">Reportés</th>
                  <th className="text-center p-2">Clients Convertis</th>
                  <th className="text-center p-2">Taux Transform.</th>
                  <th className="text-center p-2">Taux Conversion</th>
                </tr>
              </thead>
              <tbody>
                {teleprosStats.map(stats => (
                  <tr key={stats.id} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-medium">{stats.nom}</td>
                    <td className="text-center p-2">{stats.prospects_crees}</td>
                    <td className="text-center p-2">{stats.rdv_planifies}</td>
                    <td className="text-center p-2">{stats.rdv_realises}</td>
                    <td className="text-center p-2">
                      <Badge className="bg-green-100 text-green-800">{stats.rdv_signes}</Badge>
                    </td>
                    <td className="text-center p-2">
                      <Badge className="bg-red-100 text-red-800">{stats.rdv_non_signes}</Badge>
                    </td>
                    <td className="text-center p-2">{stats.rdv_annules}</td>
                    <td className="text-center p-2">{stats.rdv_reportes}</td>
                    <td className="text-center p-2">
                      <Badge className="bg-blue-100 text-blue-800">{stats.clients_convertis}</Badge>
                    </td>
                    <td className="text-center p-2">
                      <Badge className={getStatColor(parseFloat(stats.taux_transformation), 'taux')}>
                        {stats.taux_transformation}%
                      </Badge>
                    </td>
                    <td className="text-center p-2">
                      <Badge className={getStatColor(parseFloat(stats.taux_conversion_prospect), 'taux')}>
                        {stats.taux_conversion_prospect}%
                      </Badge>
                    </td>
                    <td className="text-center p-2 font-semibold text-green-700">
                      {stats.ca_total?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </td>
                    <td className="text-center p-2 font-semibold text-blue-700">
                      {stats.ca_moyen?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Statistiques Commerciaux */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-green-600" />
            Statistiques Commerciaux/Régies - Performance Ventes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Commercial/Régie</th>
                  <th className="text-center p-2">Rôle</th>
                  <th className="text-center p-2">RDV Planifiés</th>
                  <th className="text-center p-2">RDV Réalisés</th>
                  <th className="text-center p-2">Signés</th>
                  <th className="text-center p-2">Non Signés</th>
                  <th className="text-center p-2">Plantés</th>
                  <th className="text-center p-2">Reportés</th>
                  <th className="text-center p-2">Annulés</th>
                  <th className="text-center p-2">Clients Générés</th>
                  <th className="text-center p-2">CA Généré</th>
                  <th className="text-center p-2">CA Moyen</th>
                  <th className="text-center p-2">Taux Signature</th>
                </tr>
              </thead>
              <tbody>
                {commerciauxStats.map(stats => (
                  <tr key={stats.id} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-medium">{stats.nom}</td>
                    <td className="text-center p-2">
                      <Badge className={stats.role === 'commercial' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                        {stats.role}
                      </Badge>
                    </td>
                    <td className="text-center p-2">{stats.rdv_planifies}</td>
                    <td className="text-center p-2">{stats.rdv_realises}</td>
                    <td className="text-center p-2">
                      <Badge className="bg-green-100 text-green-800">{stats.rdv_signes}</Badge>
                    </td>
                    <td className="text-center p-2">
                      <Badge className="bg-red-100 text-red-800">{stats.rdv_non_signes}</Badge>
                    </td>
                    <td className="text-center p-2">
                      <Badge className="bg-orange-100 text-orange-800">{stats.rdv_plantes}</Badge>
                    </td>
                    <td className="text-center p-2">{stats.rdv_reportes}</td>
                    <td className="text-center p-2">{stats.rdv_annules}</td>
                    <td className="text-center p-2">
                      <Badge className="bg-blue-100 text-blue-800">{stats.clients_generes}</Badge>
                    </td>
                    <td className="text-center p-2 font-medium text-green-600">
                      {stats.ca_genere.toLocaleString()}€
                    </td>
                    <td className="text-center p-2 text-sm text-gray-600">
                      {stats.ca_moyen}€
                    </td>
                    <td className="text-center p-2">
                      <Badge className={getStatColor(parseFloat(stats.taux_signature), 'taux')}>
                        {stats.taux_signature}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StatistiquesPage;